// The focus outline shows only where Tab or an arrow key moved the focus.
//
// The browser's own rule (`:focus-visible`) also draws it when a script moves
// the focus after any key: Escape closing a menu or a modal hands the focus
// back to the button that opened it, and that button turned blue (the person,
// 2026-11-02). Here the outline is let through only while
// `html[data-focus-ring]` is on (styles/index.css): set when Tab or an arrow
// key moves the focus, cleared by any other key that moves it and by the
// pointer. Fields keep their soft ring either way (the caret is the mark
// there).

const NAVIGATION_KEYS = new Set([
  "Tab",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);
/** Held with Tab (Shift+Tab) or alone: they change nothing. */
const MODIFIER_KEYS = new Set(["Shift", "Control", "Alt", "Meta"]);

export function installFocusRing(target: Document = document): () => void {
  const root = target.documentElement;
  // Whether the last key or press was a move: read when the focus lands, so a
  // focus moved after Escape or Enter is not marked.
  let navigating = false;
  const onKeyDown = (e: KeyboardEvent) => {
    if (MODIFIER_KEYS.has(e.key)) return;
    navigating = NAVIGATION_KEYS.has(e.key);
    if (!navigating) root.removeAttribute("data-focus-ring");
  };
  const onPointerDown = () => {
    navigating = false;
    root.removeAttribute("data-focus-ring");
  };
  const onFocusIn = () => {
    if (navigating) root.setAttribute("data-focus-ring", "");
    else root.removeAttribute("data-focus-ring");
  };
  target.addEventListener("keydown", onKeyDown, true);
  target.addEventListener("pointerdown", onPointerDown, true);
  target.addEventListener("focusin", onFocusIn, true);
  return () => {
    target.removeEventListener("keydown", onKeyDown, true);
    target.removeEventListener("pointerdown", onPointerDown, true);
    target.removeEventListener("focusin", onFocusIn, true);
    root.removeAttribute("data-focus-ring");
  };
}
